import { useEffect, useMemo, useState } from "react";
import { toIdString } from "../../helper/applicationHelpers";

/**
 * Custom hook that manages the selection model for LinkedPropertiesPopup.
 * Encapsulates all state and toggle logic for selecting/deselecting tenants,
 * application participants, and their guarantors.
 *
 * The selection model is an array of `TenantEntry | ApplicationEntry` objects
 * (see LinkedPropertiesSelectionHelpers.js for full type definitions). A
 * derived `selectedTenant` is kept in sync via a useEffect so that all
 * downstream scoring and report code continues to work with the existing
 * shared-profile object shape.
 *
 * @param {Object}   config
 * @param {Object[]} config.propertyShareProfiles - full list of shared-profile entries
 * @param {Function} config.setStateCallback       - callback to reset UI state
 *   (typically: () => { setGiveInsurance(false); setShowRiskAssessmentReportModal(false); })
 * @returns {{
 *   selectionModel: SelectionModel,
 *   selectedTenant: Object[],
 *   setSelectionModel: Function,
 *   toggleParticipant: Function,
 *   toggleAllParticipantsInApp: Function,
 *   toggleAllEntries: Function,
 *   toggleGuarantor: Function,
 *   toggleDirectTenant: Function,
 * }}
 */
export function useSelectionModel({
  propertyShareProfiles,
  resetStateCallback,
  // Called when a change would mix company and normal tenants (refused).
  onMixedTenantTypes,
}) {
  const [selectionModel, setSelectionModel] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState([]);

  // Company login tenants (tenant_type "company") get no verdict engine and
  // can never be selected together with normal tenants, who still go through
  // the engines. Tenant ids of company tenants in this property's list:
  const companyTenantIds = useMemo(() => {
    const ids = new Set();
    (propertyShareProfiles || []).forEach((sp) => {
      if (sp?.tenantid?.tenant_type === "company" && sp?.tenantid?._id) {
        ids.add(sp.tenantid._id);
      }
      (sp?.application_id?.participants || []).forEach((p) => {
        if (p?.tenant_info?.tenant_type === "company" && p?.tenant_info?._id) {
          ids.add(p.tenant_info._id);
        }
      });
    });
    return ids;
  }, [propertyShareProfiles]);

  const isCompanyTenantId = (tenantId) => companyTenantIds.has(tenantId);

  // "company" | "normal" | null (nothing selected) for a selection model.
  const getSelectionKind = (model) => {
    const tenantIds = (model || []).flatMap((entry) =>
      entry.type === "tenant"
        ? [entry.tenantId]
        : (entry.participants || []).map((p) => p.tenantId)
    );
    if (!tenantIds.length) return null;
    const companies = tenantIds.filter((id) => companyTenantIds.has(id));
    if (!companies.length) return "normal";
    return companies.length === tenantIds.length ? "company" : "mixed";
  };
  const selectionKind = getSelectionKind(selectionModel);

  // Every toggle goes through this: a change that mixes company and normal
  // tenants is refused (the selection stays as it was).
  const updateSelection = (updater) => {
    setSelectionModel((prev) => {
      const next = updater(prev);
      if (getSelectionKind(next) === "mixed") {
        onMixedTenantTypes?.();
        return prev;
      }
      return next;
    });
  };

  const allTenantIds = useMemo(() => {
    return selectionModel
      .map((entry) => {
        if (entry.type === "tenant") {
          return {
            tenant: entry.tenantId,
            guarantors: entry.guarantorIds || [],
          };
        }
        if (entry.type === "application") {
          return {
            application: entry.applicationId,
            participants: (entry.participants || []).map((p) => ({
              tenant: p.tenantId,
              guarantors: p.guarantorIds || [],
            })),
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [selectionModel]);

  // ------------------------------------------------------------------
  // Synchronise selectedTenant from selectionModel
  // ------------------------------------------------------------------
  useEffect(() => {
    if (selectionModel.length === 0) {
      setSelectedTenant([]);
      return;
    }
    const derived = selectionModel
      .map((entry) => {
        const sharedProfile = propertyShareProfiles.find(
          (sp) => sp._id === entry.sharedProfileId
        );
        if (!sharedProfile) return null;
        if (entry.type === "tenant") return sharedProfile;
        const selectedParticipantIds = (entry.participants || []).map(
          (p) => p.tenantId
        );
        return {
          ...sharedProfile,
          application_id: {
            ...sharedProfile.application_id,
            participants: (
              sharedProfile.application_id?.participants || []
            ).filter((p) =>
              selectedParticipantIds.includes(p?.tenant_info?._id)
            ),
          },
        };
      })
      .filter(Boolean);
    setSelectedTenant(derived);
  }, [selectionModel, propertyShareProfiles]);

  // ------------------------------------------------------------------
  // Toggle: direct tenant
  // ------------------------------------------------------------------

  /**
   * Adds or removes a direct tenant from the selection model.
   * All of the tenant's guarantors are auto-selected on add.
   *
   * @param {Object} tenantData - a shared-profile entry for a direct tenant
   * @param {boolean} isChecked - whether the checkbox was checked
   */
  const toggleDirectTenant = (tenantData, isChecked) => {
    resetStateCallback();
    updateSelection((prev) => {
      if (isChecked) {
        return [
          ...prev,
          {
            sharedProfileId: tenantData._id,
            type: "tenant",
            tenantId: tenantData?.tenantid?._id,
            guarantorIds:
              tenantData?.tenantid?.gurantor_id?.map((g) => g._id) || [],
          },
        ];
      }
      return prev.filter((e) => e.sharedProfileId !== tenantData._id);
    });
  };

  // ------------------------------------------------------------------
  // Toggle: application participant
  // ------------------------------------------------------------------

  /**
   * Participants that go with this one: an application guarantor goes with
   * the tenant they back (guarantor_of), as account guarantors did. Ticking a
   * guarantor ticks their tenant; ticking a tenant ticks their accepted
   * guarantors; unticking a tenant unticks their guarantors.
   */
  const getLinkedParticipantIds = (sharedProfile, tenantId, adding) => {
    const rows = (sharedProfile?.application_id?.participants || []).filter(
      (p) => !p?.is_deleted && !p?.deleted && p?.tenant_info?._id
    );
    const idOf = (p) => toIdString(p?.id) || toIdString(p?.tenant_info?._id);
    const row = rows.find(
      (p) => String(p.tenant_info._id) === String(tenantId)
    );
    if (!row) return [];
    if (row.is_guarantor === true) {
      if (!adding) return [];
      const tenant = rows.find(
        (p) =>
          p.is_guarantor !== true && idOf(p) === toIdString(row.guarantor_of)
      );
      return tenant ? [tenant.tenant_info._id] : [];
    }
    return rows
      .filter(
        (p) =>
          p.is_guarantor === true &&
          toIdString(p.guarantor_of) === idOf(row) &&
          (!adding || p.consent_status === "accepted")
      )
      .map((p) => p.tenant_info._id);
  };

  /**
   * Toggles a single participant inside an application entry.
   * If the participant is already selected it is removed; otherwise it is
   * added, together with the participants that go with it
   * (getLinkedParticipantIds). When the last participant is deselected the
   * entire application entry is dropped from the selection model.
   *
   * @param {string} sharedProfileId     - parent shared-profile _id
   * @param {string} participantTenantId - participant's tenant_info._id
   */
  const toggleParticipant = (sharedProfileId, participantTenantId) => {
    resetStateCallback();
    updateSelection((prev) => {
      const sharedProfile = propertyShareProfiles.find(
        (sp) => sp._id === sharedProfileId
      );
      const appEntryIdx = prev.findIndex(
        (e) => e.sharedProfileId === sharedProfileId && e.type === "application"
      );
      // application guarantors are their own participant rows (the tenant's
      // account guarantors don't count in an application)
      const toParticipant = (tenantId) => ({ tenantId, guarantorIds: [] });
      if (appEntryIdx === -1) {
        const ids = [
          ...new Set([
            participantTenantId,
            ...getLinkedParticipantIds(
              sharedProfile,
              participantTenantId,
              true
            ),
          ]),
        ];
        return [
          ...prev,
          {
            sharedProfileId,
            type: "application",
            applicationId: sharedProfile?.application_id?._id,
            participants: ids.map(toParticipant),
          },
        ];
      }
      const appEntry = prev[appEntryIdx];
      const participants = appEntry.participants || [];
      const isAlreadySelected = participants.some(
        (p) => p.tenantId === participantTenantId
      );
      const linkedIds = getLinkedParticipantIds(
        sharedProfile,
        participantTenantId,
        !isAlreadySelected
      ).map(String);
      const updatedParticipants = isAlreadySelected
        ? participants.filter(
            (p) =>
              p.tenantId !== participantTenantId &&
              !linkedIds.includes(String(p.tenantId))
          )
        : [
            ...participants,
            ...[participantTenantId, ...linkedIds]
              .filter(
                (id, i, all) =>
                  all.findIndex((other) => String(other) === String(id)) ===
                    i &&
                  !participants.some((p) => String(p.tenantId) === String(id))
              )
              .map(toParticipant),
          ];

      if (updatedParticipants.length === 0) {
        return prev.filter((_, i) => i !== appEntryIdx);
      }
      const updated = [...prev];
      updated[appEntryIdx] = { ...appEntry, participants: updatedParticipants };
      return updated;
    });
  };

  // ------------------------------------------------------------------
  // Toggle: all participants in an application
  // ------------------------------------------------------------------

  /**
   * Selects all participants of an application if none or only some are
   * currently selected; deselects the entire application entry if all are
   * already selected. Auto-selects each participant's guarantors on add.
   *
   * @param {string} sharedProfileId
   * @param {Object} entry            - the full shared-profile entry
   */
  const toggleAllParticipantsInApp = (sharedProfileId, entry) => {
    resetStateCallback();
    const allParticipantIds = (entry?.application_id?.participants || [])
      .map((p) => p?.tenant_info?._id)
      .filter(Boolean);

    updateSelection((prev) => {
      const existing = prev.find(
        (e) => e.sharedProfileId === sharedProfileId && e.type === "application"
      );
      const selectedIds = (existing?.participants || []).map((p) => p.tenantId);
      const isFullySelected =
        allParticipantIds.length > 0 &&
        allParticipantIds.every((id) => selectedIds.includes(id));

      if (isFullySelected) {
        return prev.filter((e) => e.sharedProfileId !== sharedProfileId);
      }

      const newAppEntry = {
        sharedProfileId,
        type: "application",
        applicationId: entry?.application_id?._id,
        participants: allParticipantIds.map((id) => ({
          tenantId: id,
          guarantorIds: [],
        })),
      };

      const existingIdx = prev.findIndex(
        (e) => e.sharedProfileId === sharedProfileId && e.type === "application"
      );
      if (existingIdx !== -1) {
        const updated = [...prev];
        updated[existingIdx] = newAppEntry;
        return updated;
      }
      return [...prev, newAppEntry];
    });
  };

  // ------------------------------------------------------------------
  // Toggle: select / deselect all entries
  // ------------------------------------------------------------------

  /**
   * Selects every shared-profile entry (direct tenants and all participants
   * of every application) if not already all-selected; clears the entire
   * selection model if all are already selected.
   *
   * @param {Object[]} allEntries - the full list of shared-profile entries
   */
  const toggleAllEntries = (allEntriesInList) => {
    resetStateCallback();
    // Select all picks normal tenants only; a company is selected on its own.
    const allEntries = (allEntriesInList || []).filter(
      (entry) =>
        !isCompanyTenantId(entry?.tenantid?._id) &&
        !(entry?.application_id?.participants || []).some((p) =>
          isCompanyTenantId(p?.tenant_info?._id)
        )
    );
    const allSelected =
      selectionModel.length === allEntries?.length && allEntries?.length > 0;
    if (allSelected) {
      setSelectionModel([]);
      return;
    }
    const newModel = (allEntries || []).map((entry) => {
      const hasApplication =
        (entry?.application_id?.participants?.length || 0) > 0;
      if (hasApplication) {
        const participants = entry?.application_id?.participants || [];
        return {
          sharedProfileId: entry._id,
          type: "application",
          applicationId: entry?.application_id?._id,
          participants: participants.map((p) => ({
            tenantId: p?.tenant_info?._id || p?.id,
            guarantorIds: [],
          })),
        };
      }
      return {
        sharedProfileId: entry._id,
        type: "tenant",
        tenantId: entry?.tenantid?._id || entry?.tenant_id?._id,
        guarantorIds: entry?.tenantid?.gurantor_id?.map((g) => g._id) || [],
      };
    });
    // The current selection may hold a company: replace it, never mix.
    setSelectionModel(newModel);
  };

  // ------------------------------------------------------------------
  // Toggle: single guarantor
  // ------------------------------------------------------------------

  /**
   * Toggles a guarantor on/off for a specific tenant or application
   * participant. If the parent tenant/participant is not yet in the
   * selection model it is auto-added (along with the application entry
   * when the shared-profile belongs to an application).
   *
   * @param {string} sharedProfileId - parent shared-profile _id
   * @param {string} parentTenantId  - tenantId (direct) or participant.tenantId (app)
   * @param {string} guarantorId     - guarantor._id
   */
  const toggleGuarantor = (sharedProfileId, parentTenantId, guarantorId) => {
    resetStateCallback();
    updateSelection((prev) => {
      const entryIdx = prev.findIndex(
        (e) => e.sharedProfileId === sharedProfileId
      );

      if (entryIdx === -1) {
        const sharedProfile = propertyShareProfiles.find(
          (sp) => sp._id === sharedProfileId
        );
        if (!sharedProfile) return prev;

        const hasApplication =
          (sharedProfile?.application_id?.participants?.length || 0) > 0;

        if (hasApplication) {
          return [
            ...prev,
            {
              sharedProfileId,
              type: "application",
              applicationId: sharedProfile?.application_id?._id,
              participants: [
                {
                  tenantId: parentTenantId,
                  guarantorIds: [guarantorId],
                },
              ],
            },
          ];
        }

        return [
          ...prev,
          {
            sharedProfileId,
            type: "tenant",
            tenantId: parentTenantId,
            guarantorIds: [guarantorId],
          },
        ];
      }

      const entry = { ...prev[entryIdx] };

      if (entry.type === "tenant") {
        const ids = entry.guarantorIds || [];
        entry.guarantorIds = ids.includes(guarantorId)
          ? ids.filter((id) => id !== guarantorId)
          : [...ids, guarantorId];
      } else {
        const participantExists = (entry.participants || []).some(
          (p) => p.tenantId === parentTenantId
        );

        if (!participantExists) {
          entry.participants = [
            ...(entry.participants || []),
            { tenantId: parentTenantId, guarantorIds: [guarantorId] },
          ];
        } else {
          entry.participants = (entry.participants || []).map((p) => {
            if (p.tenantId !== parentTenantId) return p;
            const ids = p.guarantorIds || [];
            return {
              ...p,
              guarantorIds: ids.includes(guarantorId)
                ? ids.filter((id) => id !== guarantorId)
                : [...ids, guarantorId],
            };
          });
        }
      }

      const updated = [...prev];
      updated[entryIdx] = entry;
      return updated;
    });
  };

  return {
    selectionModel,
    selectedTenant,
    setSelectionModel,
    toggleDirectTenant,
    toggleParticipant,
    toggleAllParticipantsInApp,
    toggleAllEntries,
    toggleGuarantor,
    allTenantIds,
    selectionKind,
    isCompanyTenantId,
  };
}
